import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  useSearchBox,
  useSearchBoxDatePicker,
  useSearchBoxGuestPicker
} from 'hooks/searchBox'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {
  getDateGuestPickerOverlay,
  getIsHotelDatePickerOpen,
  getIsHotelGuestPickerOpen,
  getOverlay
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {
  DateGuestPickerSource,
  HotelDatePickerOverlayInfo,
  HotelGuestPickerOverlayInfo,
  OverlayType
} from 'modules/overlay/types'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {
  getOpenDatePickerType,
  getSearchBoxValues
} from 'modules/searchBox/selectors'
import {closeSearchBox, setSearchBoxValues} from 'modules/searchBox/slice'
import {SearchBoxValues, SearchTrigger} from 'modules/searchBox/types'

import {ANIMATION_TYPES} from '@vio/atlas/Overlay'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {ConfigureI18nContext} from '@vio/core/src/localization/components/ConfigureI18n'
import {
  roomsSplitToString,
  roomsToConfigurationObject
} from '@vio/core/src/room/business/roomConfiguration'
import {
  RoomConfigType,
  RoomSplitType
} from '@vio/core/src/room/types/RoomConfiguration'
import {WithColorPriceDayData} from '@vio/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData'
import {usePreviousValue} from '@vio/core/src/utils/react/hooks/usePreviousValue'
import {DatePickerOverlay} from '@vio/shared/src/search/DatePickerOverlay'
import {GuestRoomConfigOverlay} from '@vio/shared/src/search/GuestRoomConfig/GuestRoomConfigOverlay'

// Hook that triggers a search *after* the picker overlay closes (and it's browser history entry is removed)
// this allows us to replace the AP and ensure browser-back goes directly to SRP
const useDeferredSearch = () => {
  const overlay = useSelector(getOverlay)
  const currentHotelId = useSelector(getAccommodationPageHotelId)
  const isOpenedDatePicker = useSelector(getIsHotelDatePickerOpen)
  const isOpenedGuestPicker = useSelector(getIsHotelGuestPickerOpen)
  const pendingSearchRef = useRef<null | SearchTrigger>(null)
  const pickerIsOpen = isOpenedDatePicker || isOpenedGuestPicker
  const pickerWasClosed = usePreviousValue(pickerIsOpen) && !pickerIsOpen

  const setSearchPending = useCallback((value: null | SearchTrigger) => {
    pendingSearchRef.current = value
  }, [])

  const {doSearch} = useSearchBox()
  useEffect(() => {
    if (pickerWasClosed && pendingSearchRef.current) {
      doSearch({
        searchTrigger: pendingSearchRef.current,
        searchOptions: {
          method: 'replace',
          state: {}
        },
        newUrlParams: {
          placeId: undefined,
          boundingBox: undefined,
          userLocationSearch: undefined,
          hotelId: currentHotelId
        }
      })

      setSearchPending(null)
    }
  }, [overlay, pickerWasClosed, setSearchPending, doSearch])

  return setSearchPending
}

const searchTriggerMap = {
  [DateGuestPickerSource.AccommodationPageSearch]: {
    [OverlayType.HotelDatePicker]: SearchTrigger.AccommodationPageDatePicker,
    [OverlayType.HotelGuestPicker]: SearchTrigger.AccommodationPageGuestPicker
  },
  [DateGuestPickerSource.HotelViewRoomsSection]: {
    [OverlayType.HotelDatePicker]:
      SearchTrigger.AccommodationPageRoomsDatePicker,
    [OverlayType.HotelGuestPicker]:
      SearchTrigger.AccommodationPageRoomsGuestPicker
  }
}

export const DateGuestOverlayContainer = () => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {type, source} = useSelector(getDateGuestPickerOverlay) as
    | HotelDatePickerOverlayInfo
    | HotelGuestPickerOverlayInfo

  const searchTrigger = searchTriggerMap[source]?.[type]

  const {monthNames, weekDayNamesShort, firstDayOfWeek} =
    useContext(ConfigureI18nContext)

  const isOpenedDatePicker = useSelector(getIsHotelDatePickerOpen)
  const isOpenedGuestPicker = useSelector(getIsHotelGuestPickerOpen)
  const openedDatePickerType = useSelector(getOpenDatePickerType)
  const {
    checkIn: searchParametersCheckIn,
    checkOut: searchParametersCheckOut,
    rooms: searchParametersRooms,
    optimizeRooms: searchParametersOptimizeRooms
  } = useSelector(getSearchParameters)

  const {checkIn, checkOut, rooms, optimizeRooms} =
    useSelector(getSearchBoxValues)

  const previousIsOpen = usePreviousValue(
    isOpenedDatePicker || isOpenedGuestPicker
  )
  const [initialValues, setInitialValues] =
    useState<Pick<SearchBoxValues, 'checkIn' | 'checkOut' | 'rooms'>>()

  useEffect(() => {
    dispatch(
      setSearchBoxValues({
        checkIn: searchParametersCheckIn,
        checkOut: searchParametersCheckOut,
        rooms: searchParametersRooms
      })
    )
  }, [
    dispatch,
    searchParametersCheckIn,
    searchParametersCheckOut,
    searchParametersRooms
  ])

  useEffect(() => {
    const isOpen = isOpenedDatePicker || isOpenedGuestPicker

    if (previousIsOpen && !isOpen && initialValues) {
      dispatch(setSearchBoxValues(initialValues))
    }

    if (!previousIsOpen && isOpen && checkIn && checkOut) {
      setInitialValues({
        checkIn,
        checkOut,
        rooms
      })
    }
  }, [
    dispatch,
    rooms,
    checkIn,
    checkOut,
    initialValues,
    isOpenedDatePicker,
    isOpenedGuestPicker,
    previousIsOpen
  ])

  const {roomsSplit} = roomsToConfigurationObject(rooms)

  const initialDatePickerValues = {
    checkIn: searchParametersCheckIn,
    checkOut: searchParametersCheckOut
  }
  const initialGuestPickerValues = {
    rooms: searchParametersRooms,
    optimizeRooms: searchParametersOptimizeRooms
  }

  const {onDateChange, onDatePickerDayClick, onDatePickerOpen} =
    useSearchBoxDatePicker(initialDatePickerValues)
  const {
    onGuestRoomsChange,
    onGuestPickerOptionInfoDisplayed,
    onGuestConfigPreferenceClick,
    onGuestOptimizeRoomsChange
  } = useSearchBoxGuestPicker(initialGuestPickerValues)
  const setPendingSearch = useDeferredSearch()

  const onGuestPickerOverlaySubmit = useCallback(
    (roomsSplit: RoomSplitType[]): void => {
      const rooms = roomsSplitToString(roomsSplit)

      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.GuestPickerSubmitButton,
          action: Action.Clicked
        })
      )

      onGuestRoomsChange(rooms)

      setPendingSearch(searchTrigger)
      dispatch(hideOverlay())
      dispatch(closeSearchBox())
    },
    [
      dispatch,
      dispatchTeamEvent,
      setPendingSearch,
      onGuestRoomsChange,
      searchTrigger
    ]
  )

  const onDatePickerOverlaySubmit = useCallback((): void => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.DatePickerSubmitButton,
        action: Action.Clicked
      })
    )

    setPendingSearch(searchTrigger)
    dispatch(hideOverlay())
    dispatch(closeSearchBox())
  }, [dispatch, dispatchTeamEvent, setPendingSearch, searchTrigger])

  const closeDatePicker = () => {
    dispatch(hideOverlay())
  }

  const closeGuestPicker = () => {
    dispatch(hideOverlay())
  }

  const handleGuestOptimizeRoomsChange = useCallback(
    (isOptimizeRooms: boolean): void => {
      onGuestOptimizeRoomsChange(isOptimizeRooms)
    },
    [onGuestOptimizeRoomsChange]
  )

  const handleGuestRoomsChange = useCallback(
    (roomsSplit: RoomSplitType[]): void => {
      const rooms = roomsSplitToString(roomsSplit)
      onGuestRoomsChange(rooms)
    },
    [onGuestRoomsChange]
  )

  return (
    <>
      <DatePickerOverlay
        isOpen={isOpenedDatePicker}
        checkIn={checkIn}
        checkOut={checkOut}
        firstDayOfWeek={firstDayOfWeek}
        months={monthNames}
        openedDatePickerType={openedDatePickerType}
        weekdaysShort={weekDayNamesShort}
        onChange={onDateChange}
        onOpen={onDatePickerOpen}
        onCheckOutSelected={() => console.log}
        onDayClick={onDatePickerDayClick}
        onSubmit={onDatePickerOverlaySubmit}
        onClose={closeDatePicker}
        animationType={ANIMATION_TYPES.NONE}
        DayInnerContainer={WithColorPriceDayData}
      />
      <GuestRoomConfigOverlay
        isOpen={isOpenedGuestPicker}
        roomsSplit={roomsSplit}
        onClose={closeGuestPicker}
        onRoomsChange={handleGuestRoomsChange}
        onOptimizeRoomsChange={handleGuestOptimizeRoomsChange}
        onSubmit={onGuestPickerOverlaySubmit}
        onInfoOpen={onGuestPickerOptionInfoDisplayed}
        onGuestConfigOptionClick={onGuestConfigPreferenceClick}
        roomConfigType={
          optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual
        }
        animationType={ANIMATION_TYPES.NONE}
      />
    </>
  )
}
